// alterColumn — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-column', change: { type?, nullable?, default? } })`
// op (DSE2 §3.A1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice A1.
//
// Pipeline (matches the Codebase Pattern → "Commit pipeline"):
//   buildAlterColumnEdit(args) → DdlEdit
//   commitAlterColumn(ctx, args) → commitDdlEdit(edit, toCommitCtx(ctx))
//
// Per-engine support is gated by `isAlterColumnSupported(engine)`. SQLite throws
// in DSE2's synthesizer (recreate path is opt-in for slice H3); the UI consults
// `isAlterColumnSupported` to render a disabled affordance with the
// `getAlterColumnUnsupportedTooltip(engine)` text instead of attempting the
// commit and surfacing a runtime error. mssql + mysql require the column type
// even when only changing nullability — `requiresFullTypeReclaration(engine)`
// drives the form's type-field requirement so the user can't submit an edit
// the engine will refuse.

import type { ExistingNameOrigin } from '../../../../sql-resolver/ddl/apply/dialect.quoting';
import type { ColumnChange, DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AlterColumnArgs = {
  readonly columnName: string;
  readonly change: ColumnChange;
  /** WHICH occurrence `columnName` names. A source may declare one byte-name
   *  twice with different delimiters (`CREATE TABLE t (Id integer, "Id"
   *  integer)`) — two different columns on a folding engine — and then the
   *  name alone cannot say which is meant, so `applyDdlEdit` refuses rather
   *  than guessing. The per-row Edit affordance is never in that position: the
   *  row IS the occurrence, and `ColumnRow.nameOrigin` carries the spelling
   *  the source declared it with straight into the payload. Omitted by a
   *  caller that holds only a name — then the source statement answers, and
   *  refuses when it declares both. */
  readonly columnNameOrigin?: ExistingNameOrigin;
  /** mssql-only — see `DdlEdit.alter-column` field docs in DSE2. */
  readonly mssqlExistingDefaultConstraintName?: string;
  readonly mssqlNewDefaultConstraintName?: string;
};

export function buildAlterColumnEdit(args: AlterColumnArgs): DdlEdit {
  return {
    op: 'alter-column',
    columnName: args.columnName,
    change: args.change,
    columnNameOrigin: args.columnNameOrigin,
    mssqlExistingDefaultConstraintName: args.mssqlExistingDefaultConstraintName,
    mssqlNewDefaultConstraintName: args.mssqlNewDefaultConstraintName,
  };
}

export async function commitAlterColumn(
  ctx: VqeDdlEditContext,
  args: AlterColumnArgs,
): Promise<void> {
  const edit = buildAlterColumnEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAlterColumnSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getAlterColumnUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … ALTER COLUMN. Recreate the '
      + 'table with the new column definition — use “Edit as SQL” to write '
      + 'the change.'
    );
  }
  return '';
}

/** mssql + mysql require a full type re-statement on every alter-column edit
 *  even when the user only changes nullability or default. The UI uses this to
 *  mark the type field required and block submit when empty. */
export function requiresFullTypeReclaration(engine: SqlEngine): boolean {
  return engine === 'mssql' || engine === 'mysql';
}

/** True when the supplied change is non-empty — at least one of type, nullable,
 *  or default is specified. Used by the form to enable the Submit button. */
export function isNonEmptyColumnChange(change: ColumnChange): boolean {
  return (
    change.type !== undefined
    || change.nullable !== undefined
    || change.default !== undefined
    || change.identity !== undefined
  );
}
